import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/authUtils';

const API = getApiUrl();

/**
 * Canonical agency statistics for Platform Detail + Agency Dashboard cards.
 * Prefer owner-console detail payload when already loaded; otherwise fetch
 * /owner-console/agencies/:id/statistics or /portal/staff/dashboard.
 */
export function useAgencyStatistics({
  agencyId,
  token,
  enabled = true,
  mode = 'auto', // 'platform' | 'agency' | 'auto'
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled && agencyId));
  const [error, setError] = useState('');
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    if (!agencyId || !enabled) {
      setLoading(false);
      return;
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    setError('');
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let response;
      if (mode === 'agency') {
        response = await axios.get(`${API}/portal/staff/dashboard`, {
          headers,
          withCredentials: true,
        });
      } else {
        try {
          response = await axios.get(
            `${API}/owner-console/agencies/${agencyId}/statistics`,
            { headers, withCredentials: true },
          );
        } catch (platformErr) {
          if (mode === 'platform') throw platformErr;
          // Agency operators fall back to staff dashboard.
          response = await axios.get(`${API}/portal/staff/dashboard`, {
            headers,
            withCredentials: true,
          });
        }
      }
      if (seq !== requestSeq.current) return;
      const payload = response.data || {};
      const stats = payload.statistics || payload;
      const summary = payload.summary || {};
      const queues = payload.queues || {};
      setData({
        agency_id: stats.agency_id || agencyId,
        total_users: stats.total_users ?? stats.user_stats?.total_users ?? stats.user_stats?.total ?? null,
        team_members: stats.team_members ?? stats.user_stats?.team_members ?? null,
        staff: stats.staff ?? stats.user_stats?.staff ?? null,
        owners: stats.owners ?? stats.user_stats?.owners ?? null,
        managed_clients:
          stats.managed_clients
          ?? stats.user_stats?.managed_clients
          ?? stats.user_stats?.clients
          ?? null,
        active_cases:
          stats.active_cases
          ?? summary.active_cases
          ?? stats.case_stats?.active
          ?? null,
        my_assigned:
          stats.my_assigned
          ?? summary.my_assigned_cases
          ?? null,
        unassigned:
          stats.unassigned
          ?? summary.unassigned_cases
          ?? null,
        active_disputes:
          stats.active_disputes
          ?? queues.active_disputes
          ?? null,
        total_cases: stats.total_cases ?? summary.total_cases ?? stats.case_stats?.total ?? null,
        raw: payload,
      });
    } catch (requestError) {
      if (seq !== requestSeq.current) return;
      setError(
        requestError?.response?.data?.detail
        || requestError?.message
        || 'Unable to load agency statistics',
      );
      setData(null);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [agencyId, enabled, mode, token]);

  useEffect(() => {
    load();
    return () => {
      requestSeq.current += 1;
    };
  }, [load]);

  return {
    data,
    loading,
    error,
    refresh: load,
  };
}

export default useAgencyStatistics;
